// hooks/useQuizRating.js
import { useState } from 'react';
import { doc, setDoc, updateDoc, getDocs, query, where, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase-config';

export const useQuizRating = (quizId, currentUser) => {
    const [rating, setRating] = useState(0);
    const [feedback, setFeedback] = useState('');
    const [hasRated, setHasRated] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const submitRating = async () => {
        if (!currentUser?.uid || !quizId || !rating) return;

        try {
            setSubmitting(true);
            const feedbackRef = doc(db, 'quiz_feedback', `${quizId}_${currentUser.uid}`);

            await setDoc(feedbackRef, {
                quizId,
                userId: currentUser.uid,
                userDisplayName: currentUser.displayName,
                userPhotoURL: currentUser.photoURL,
                profilePath: currentUser.profilePath !== 'guest' ? currentUser.profilePath : null,
                rating: Number(rating),
                feedback: feedback || '',
                timestamp: serverTimestamp(),
                updatedAt: serverTimestamp()
            });

            // Update quiz rating statistics
            const quizRef = doc(db, 'quizzes', quizId);
            const feedbackQuery = query(
                collection(db, 'quiz_feedback'),
                where('quizId', '==', quizId)
            );

            const feedbackSnapshot = await getDocs(feedbackQuery);
            const ratings = feedbackSnapshot.docs.map(doc => doc.data().rating);

            const averageRating = ratings.reduce((a, b) => a + b, 0) / ratings.length;
            const distribution = {
                1: ratings.filter(r => r === 1).length,
                2: ratings.filter(r => r === 2).length,
                3: ratings.filter(r => r === 3).length,
                4: ratings.filter(r => r === 4).length,
                5: ratings.filter(r => r === 5).length
            };

            const totalRatings = ratings.length;
            const positiveRatings = ratings.filter(r => r >= 4).length;
            const positiveRatingPercent = (positiveRatings / totalRatings) * 100;

            await updateDoc(quizRef, {
                'stats.averageRating': averageRating,
                'stats.totalRatings': totalRatings,
                'stats.positiveRatingPercent': positiveRatingPercent,
                'stats.ratingDistribution': distribution,
                lastUpdated: serverTimestamp()
            });

            setHasRated(true);
        } catch (error) {
            console.error('Error submitting rating:', error);
            setError('Error saving rating: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    return {
        rating,
        setRating,
        feedback,
        setFeedback,
        hasRated,
        submitting,
        error,
        submitRating
    };
};